import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import { FormContainer as Form } from 'components/shared/form/Form';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useGroupOptionLabel from 'hooks/useGroupOptionLabel';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminIndexCredentials } from 'services/api/admin/credentials';
import { adminIndexGroups } from 'services/api/admin/groups';
import { adminIndexRoles } from 'services/api/admin/roles';
import { adminCreateUser, adminShowUser, adminUpdateUser, adminUsersQueryKeys } from 'services/api/admin/users';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

// The update endpoint surfaces validation failures as `data.errors` (field -> messages),
// while create re-raises them as a single `data.error` string. Handle both so the toast
// shows the actual reason (e.g. a group or provider type invalid for the new client).
const saveErrorMessage = (err: any): string => {
  const data = err?.response?.data;
  if (data?.error) return data.error;
  if (data?.errors) return Object.values<string[]>(data.errors).flat().join(' ');
  return 'There was an error saving the user. Please try again.';
};

const ADMIN_CLIENT_TYPE = 'Admin';
const FORBIDDEN_ROLES = ['Olio Admin', 'View Only Olio Admin', 'Bot'];
const FORBIDDEN_ROLES_ADMIN = ['Bot'];

const userSchema = yup.object({
  name: yup.string().required('User name is required'),
  email: yup
    .string()
    .required('Valid email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Valid email is required')
    .test('admin-domain', 'Users associated to an Admin client must use the "@olio.health" domain', (value, ctx) => {
      if (!value) return true;
      if (ctx.parent.client?.clientType !== ADMIN_CLIENT_TYPE) return true;
      return value.endsWith('@olio.health');
    }),
  role: yup
    .mixed()
    .nullable()
    .test('role', 'Role is required', (v: any) => !!v?.id),
  client: yup
    .mixed()
    .nullable()
    .test('client', 'Client is required', (v: any) => !!v?.id),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditUser() {
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const canEdit = profile.has(PERMISSIONS.adminUserEdit);
  const disabled = !canEdit;

  // --- Data + form ---

  const form = useAdminForm({
    id,
    show: adminShowUser,
    create: async (payload: any) => {
      try {
        return await adminCreateUser(payload);
      } catch (err: any) {
        addToast({ text: saveErrorMessage(err) });
        throw err;
      }
    },
    update: async (updateId: string, payload: any) => {
      try {
        return await adminUpdateUser(updateId, payload);
      } catch (err: any) {
        addToast({ text: saveErrorMessage(err) });
        throw err;
      }
    },
    include: [
      'actingClient',
      'client',
      'client.providedProviderTypes',
      'client.managedProviderTypes',
      'credential',
      'enabledProviderTypes',
      'role',
      'selectedGroups',
      'selectedProvidedProviderTypes',
      'selectedManagedProviderTypes',
    ] as const,
    queryKey: adminUsersQueryKeys,
    schema: userSchema,
    onCreateSuccess: { toast: 'User created successfully.', navigate: '/admin/users' },
    onUpdateSuccess: { toast: 'User updated successfully.', navigate: '/admin/users' },
    toFormValues: (data) => ({
      id: data?.id ?? '',
      name: data?.name ?? '',
      email: data?.email ?? '',
      credential: data?.credential ?? null,
      client: data?.client ?? null,
      actingClient: data?.actingClient ?? null,
      role: data?.role ?? null,
      groupType: data?.groupType ?? null,
      selectedManagedProviderTypes: data?.selectedManagedProviderTypes ?? [],
      selectedProvidedProviderTypes: data?.selectedProvidedProviderTypes ?? [],
      selectedGroups: data?.selectedGroups ?? [],
      sendInvite: true,
    }),
    toPayload: (v) => ({
      id: v.id || undefined,
      active: true,
      clientId: v.client?.id,
      credentialId: v.credential?.id || '',
      email: v.email,
      groupType: v.groupType ?? undefined,
      groupIds: v.selectedGroups.map((g) => g.id),
      name: v.name,
      roleId: v.role?.id,
      selectedManagedProviderTypeIds: v.selectedManagedProviderTypes.map((p) => p.id),
      selectedProvidedProviderTypeIds: v.selectedProvidedProviderTypes.map((p) => p.id),
      ...(v.actingClient && v.client?.id !== v.actingClient.id
        ? { actingClientId: v.actingClient.id }
        : { actingClientId: null }),
      // Control flag honored by the create endpoint only; omit on update.
      ...(v.id ? {} : { sendInvite: v.sendInvite }),
    }),
  });

  const { data, values, errors, dirty, setValue, validateField, submit, loading, submitting, submitAndReset, goBack } =
    form;
  const valid = !!values && userSchema.isValidSync(values);

  // --- Dropdown options ---

  const client = values?.client;
  const isAdmin = client?.clientType === ADMIN_CLIENT_TYPE;
  const isLeaf = !!client?.leaf;
  const groupIdsKey = (groups?: { id: string }[]) =>
    (groups ?? [])
      .map((g) => g.id)
      .sort()
      .join(',');
  const groupsChanged = groupIdsKey(values?.selectedGroups) !== groupIdsKey(data?.selectedGroups);

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'users'],
    fetchFn: adminIndexClients,
    params: { sortBy: 'name asc', include: 'managedProviderTypes,providedProviderTypes' },
  });

  const groupOpts = useInfiniteOptions({
    queryKey: ['adminGroups', 'users', client?.id],
    fetchFn: adminIndexGroups,
    params: { clientId: client?.id, include: 'groupType,address,client', sortBy: 'name asc' },
  });

  const groupLabelProps = useGroupOptionLabel();

  const credentialOpts = useInfiniteOptions({
    queryKey: ['adminCredentials', 'users'],
    fetchFn: adminIndexCredentials,
    params: { sortBy: 'title asc' },
  });

  const forbiddenList = (client?.clientType === ADMIN_CLIENT_TYPE ? FORBIDDEN_ROLES_ADMIN : FORBIDDEN_ROLES).join(',');
  const roleOpts = useInfiniteOptions({
    queryKey: ['adminRoles', 'users', forbiddenList],
    fetchFn: adminIndexRoles,
    params: { 'name.not': forbiddenList, sortBy: 'name asc' },
  });

  // Reset dependent fields when client changes (create mode only)
  const prevClientId = useRef<string | undefined>(client?.id);
  useEffect(() => {
    if (!isEditing && prevClientId.current !== client?.id) {
      prevClientId.current = client?.id;
      setValue('role', null);
      validateField('name');
      validateField('email');
      setValue('selectedGroups', []);
      setValue('selectedManagedProviderTypes', []);
      setValue('selectedProvidedProviderTypes', []);
      setValue('groupType', null);
    }
  });

  const showProviderTypes = !isAdmin && isLeaf;

  // --- Render ---

  if (isEditing && loading) return <CircularProgress />;

  return (
    <AdminFormPage>
      <FormHeader title={isEditing ? 'Edit User' : 'New User'} />
      <Form>
        <FormSection>
          <SectionHeader>Contact Information</SectionHeader>
          <InputGroup title='Name' error={getFieldError(errors, 'name')}>
            <Input
              value={values?.name ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('name', e.target.value)}
              placeholder='User name'
              disabled={disabled}
            />
          </InputGroup>
          <InputGroup title='Credentials (optional)'>
            <Select
              {...credentialOpts}
              value={values?.credential ?? null}
              onChange={(v) => setValue('credential', v)}
              getOptionValue={getId}
              getOptionLabel={(o) => o!.title}
              isClearable
              placeholder='Select credentials'
              isDisabled={disabled}
            />
          </InputGroup>
          <InputGroup title='Email' error={getFieldError(errors, 'email')}>
            <Input
              value={values?.email ?? ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue('email', e.target.value)}
              placeholder='your_email@example.com'
              disabled={disabled}
            />
          </InputGroup>
        </FormSection>

        <FormSection>
          <SectionHeader>User Access</SectionHeader>
          <InputGroup title='Client' error={getFieldError(errors, 'client')}>
            <Select
              {...clientOpts}
              value={values?.client ?? null}
              onChange={(v) => {
                setValue('client', v);
                setValue('actingClient', v);
              }}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select client'
              isDisabled={disabled}
            />
          </InputGroup>
          {isEditing && (
            <InputGroup title='Current Selected View'>
              <Select
                options={[]}
                value={values?.actingClient ?? null}
                onChange={() => {}}
                getOptionValue={getId}
                getOptionLabel={getName}
                placeholder=''
                isDisabled
              />
            </InputGroup>
          )}
          <InputGroup title='Role' error={getFieldError(errors, 'role')}>
            <Select
              {...roleOpts}
              value={values?.role ?? null}
              onChange={(v) => setValue('role', v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Select role'
              isDisabled={disabled || !client}
            />
          </InputGroup>
          {showProviderTypes && (
            <>
              <InputGroup>
                <MultiSelectChips
                  title='Associated Managed Care Options'
                  options={client?.managedProviderTypes ?? []}
                  values={values?.selectedManagedProviderTypes ?? []}
                  onChange={(v) => setValue('selectedManagedProviderTypes', v)}
                  getOptionValue={getId}
                  getOptionLabel={getDisplayName}
                  placeholder='All Managed Care Options'
                  disabled={disabled}
                />
              </InputGroup>
              <InputGroup>
                <MultiSelectChips
                  title='Associated Provided Care Options'
                  options={client?.providedProviderTypes ?? []}
                  values={values?.selectedProvidedProviderTypes ?? []}
                  onChange={(v) => setValue('selectedProvidedProviderTypes', v)}
                  getOptionValue={getId}
                  getOptionLabel={getDisplayName}
                  placeholder='All Provided Care Options'
                  disabled={disabled}
                />
              </InputGroup>
            </>
          )}
          {!isAdmin && isLeaf && (
            <InputGroup>
              <MultiSelectChips
                title='Associated Groups (select all that apply)'
                {...groupOpts}
                {...groupLabelProps}
                values={values?.selectedGroups ?? []}
                onChange={(v) => setValue('selectedGroups', v)}
                placeholder='All Groups'
                disabled={disabled}
              />
              {groupsChanged && (
                <Alert style={{ marginTop: 16 }}>
                  <Alert.Text>
                    Access changes may not take effect immediately. Once they do, the Groups column on the Users list
                    page will be updated on next page load to reflect current access.
                  </Alert.Text>
                </Alert>
              )}
            </InputGroup>
          )}
          {!isEditing && (
            <InputGroup>
              <Checkbox
                checked={values?.sendInvite ?? true}
                onChange={(val) => setValue('sendInvite', val)}
                label='Send invite now'
                labelSize='14px'
                checkedColor={colors.blue100}
                size={16}
                disabled={disabled}
                dataCyLabel='sendInvite'
              />
            </InputGroup>
          )}
        </FormSection>

        <div style={{ display: 'flex', width: '100%' }}>
          {!isEditing && (
            <Button
              variant='outline-primary'
              onClick={() => submitAndReset(['name', 'email', 'credential'])}
              disabled={disabled || !dirty || !valid || submitting}>
              Save and Add Another
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={goBack} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={disabled || !dirty || !valid || submitting}>
              {isEditing ? 'Update User' : 'Create User'}
            </Button>
          </ButtonGroup>
        </div>
      </Form>
    </AdminFormPage>
  );
}
